<template>
  <div
    class="absolute top-0 left-0 w-full h-full bg-black z-[11001] bg-opacity-20 flex"
    @click.self="emit('close')"
  >
    <div
      class="mx-auto mt-24 mb-auto bg-body rounded border-2 border-primary w-[900px] max-w-[90vw] h-[70vh] max-h-[640px] flex flex-row overflow-hidden"
    >
      <!-- Left rail: saved conversations (sessions panel) -->
      <div class="w-56 shrink-0 border-r border-body-darker flex flex-col bg-body-lighter">
        <button
          type="button"
          class="flex items-center gap-2 m-2 px-3 py-2 rounded border border-body-darker hover:border-primary text-body-content text-sm"
          :disabled="isBusy"
          @click="startNewChat"
        >
          <AppIcon icon="fa-plus" fixed-width />
          Nieuw gesprek
        </button>
        <ul class="list-none overflow-y-auto scrollbar-thin grow px-2 pb-2 flex flex-col gap-1">
          <li
            v-for="conversation in conversations"
            :key="conversation.id"
            class="conversation group"
            :class="{ 'conversation--active': conversation.id === activeConversationId }"
          >
            <button
              type="button"
              class="conversation__title"
              :disabled="isBusy"
              @click="selectConversation(conversation.id)"
            >
              {{ conversation.title }}
            </button>
            <AppIcon
              v-tooltip="'Verwijderen'"
              icon="fa-trash"
              class="conversation__delete"
              @click.stop="removeConversation(conversation.id)"
            />
          </li>
          <li
            v-if="!conversations.length"
            class="text-xs text-body-content opacity-60 px-2 py-3"
          >
            Nog geen gesprekken.
          </li>
        </ul>
      </div>

      <!-- Right pane: question input, transcript, hints -->
      <div class="flex-1 flex flex-col min-w-0">
        <!-- Question input -->
        <div class="flex items-center bg-body-lighter relative shadow-md">
          <AppIcon
            :icon="isBusy ? 'spinner' : 'far fa-robot'"
            :spin-pulse="isBusy"
            fixed-width
            class="absolute left-3 h-4 text-body-content"
          />
          <input
            ref="searchInput"
            v-model="searchValue"
            v-maintain-focus
            :disabled="isBusy"
            class="p-3 pl-10 pr-16 w-full focus:outline-none bg-body-lighter text-body-content"
            placeholder="Vraag iets over je data…"
            @keydown.escape.stop.prevent="emit('close')"
            @keyup.enter.exact.stop="onSubmit"
          >
          <KeyboardKey
            class="cursor-pointer select-none absolute right-3 text-xs hover:text-primary"
            @click="emit('close')"
          >
            Esc
          </KeyboardKey>
        </div>

        <!-- Screen-context chip: what the assistant will assume when the question names no subject. -->
        <button
          v-if="screenContext"
          v-tooltip="useScreenContext
            ? translate('common.messages.Answer without using the screen as context')
            : translate('common.messages.Using the screen you have open as context')"
          type="button"
          class="context-chip"
          :class="{ 'context-chip--off': !useScreenContext }"
          :disabled="isBusy"
          @click="useScreenContext = !useScreenContext"
        >
          <AppIcon
            :icon="useScreenContext ? 'far fa-eye' : 'far fa-eye-slash'"
            fixed-width
            class="h-3"
          />
          <span class="truncate">{{ translate("common.words.Context") }}: {{ screenContext.summary }}</span>
          <AppIcon
            v-if="screenContext.hasFilter"
            icon="far fa-filter"
            fixed-width
            class="h-3 shrink-0"
          />
        </button>

        <!-- Conversation transcript -->
        <div
          ref="transcriptEl"
          class="grow overflow-y-auto scrollbar-thin p-3 flex flex-col gap-3 border-t border-body-darker"
        >
          <div
            v-if="!messages.length"
            class="m-auto text-sm text-body-content opacity-50 text-center px-6"
          >
            Stel een vraag over je verkooporders, klanten of producten.
            Vervolgvragen onthoud ik binnen hetzelfde gesprek.
          </div>
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="msg-row"
            :class="message.role === 'user' ? 'msg-row--user' : 'msg-row--assistant'"
          >
            <!-- Assistant tool-step progress: distinct live bubbles while thinking, collapsed once done. -->
            <template v-if="message.role === 'assistant' && message.steps.length">
              <template v-if="isThinking(message, index)">
                <div
                  v-for="(step, stepIndex) in message.steps"
                  :key="stepIndex"
                  class="step-bubble"
                >
                  <AppIcon
                    :icon="stepIndex === message.steps.length - 1 ? 'spinner' : 'fa-check'"
                    :spin-pulse="stepIndex === message.steps.length - 1"
                    fixed-width
                  />
                  {{ step }}
                </div>
              </template>
              <details
                v-else
                class="steps-toggle"
              >
                <summary>Toon stappen</summary>
                <div class="flex flex-col gap-1 mt-1">
                  <div
                    v-for="(step, stepIndex) in message.steps"
                    :key="stepIndex"
                    class="step-bubble"
                  >
                    <AppIcon icon="fa-check" fixed-width />
                    {{ step }}
                  </div>
                </div>
              </details>
            </template>

            <!-- Message body: user text / assistant answer / error / thinking placeholder -->
            <div
              v-if="message.role !== 'assistant' || message.html || showThinkingPlaceholder(message, index)"
              class="message"
              :class="{
                'message--user': message.role === 'user',
                'message--assistant': message.role === 'assistant',
                'message--error': message.role === 'error',
              }"
            >
              <template v-if="message.role === 'assistant'">
                <!-- eslint-disable-next-line vue/no-v-html -- markdown-it runs with html:false, so output is escaped/safe -->
                <div
                  v-if="message.html"
                  class="markdown"
                  v-html="message.html"
                />
                <span
                  v-else
                  class="flex items-center gap-2 opacity-70"
                >
                  <AppIcon icon="spinner" spin-pulse fixed-width />
                  Bezig met nadenken…
                </span>
              </template>
              <template v-else>
                {{ message.text }}
              </template>
            </div>
          </div>
        </div>

        <!-- Hint bar -->
        <div
          class="flex items-center gap-4 px-3 py-2 text-xs text-body-content opacity-60 border-t border-body-darker bg-body"
        >
          <span class="flex items-center gap-1"><KeyboardKey>Enter</KeyboardKey> vraag stellen</span>
          <span class="flex items-center gap-1"><KeyboardKey>Esc</KeyboardKey> sluiten</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MarkdownIt from "markdown-it";
import { vMaintainFocus } from "~/directives/vMaintainFocus";
import { useAssistant } from "~/composables/useAssistant";
import { useAssistantContext, type ResolvedScreenContext } from "~/composables/useAssistantContext";
import { translate } from "~/plugins/I18n";
import { useAssistantStore } from "~/stores/Assistant";

interface ChatMessage {
	role: "user" | "assistant" | "error";
	text: string;
	html?: string;
	steps: string[];
}

const emit = defineEmits(["close"]);

const { askStreamInConversation, getTranscript } = useAssistant();
const { resolve: resolveScreenContext } = useAssistantContext();
const store = useAssistantStore();
const { conversations, activeConversationId } = storeToRefs(store);
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const searchInput = ref<HTMLInputElement | null>();
const transcriptEl = ref<HTMLElement | null>();
const searchValue = ref("");
const isBusy = ref(false);
const messages = ref<ChatMessage[]>([]);

// The screen behind the palette, and whether to send it. Re-resolved before every question: the user can
// switch tabs between two turns of the same conversation.
const screenContext = ref<ResolvedScreenContext | undefined>();
const useScreenContext = ref(true);

onMounted(() => {
	store.refresh();
	screenContext.value = resolveScreenContext();
});

/** The last assistant message is still "thinking" until its answer text (html) starts arriving. */
function isThinking(message: ChatMessage, index: number): boolean {
	return isBusy.value && index === messages.value.length - 1 && !message.html;
}

/** Show the "Bezig met nadenken…" bubble only before the first tool-step arrives. */
function showThinkingPlaceholder(message: ChatMessage, index: number): boolean {
	return isThinking(message, index) && message.steps.length === 0;
}

async function onSubmit() {
	const question = searchValue.value.trim();
	if (!question || isBusy.value) return;

	isBusy.value = true;
	screenContext.value = resolveScreenContext();
	const context = useScreenContext.value ? screenContext.value?.context : undefined;

	// First question in a fresh chat persists a new conversation titled by the question.
	let conversationId = activeConversationId.value;
	if (!conversationId) {
		try {
			conversationId = await store.start(question);
		} catch {
			isBusy.value = false;
			useToast().error("Kon gesprek niet aanmaken.");
			return;
		}
	}

	messages.value.push({ role: "user", text: question, steps: [] });
	messages.value.push({ role: "assistant", text: "", html: "", steps: [] });
	// Grab the reactive proxy of the just-pushed assistant message so mutations stream into the UI.
	const answer = messages.value[messages.value.length - 1];

	searchValue.value = "";
	scrollToBottom();

	try {
		await askStreamInConversation(
			conversationId,
			question,
			{
				onProgress: (label) => {
					answer.steps.push(label);
					scrollToBottom();
				},
				onToken: (text) => {
					answer.text += text;
					answer.html = md.render(answer.text);
					scrollToBottom();
				},
				onError: (message) => {
					answer.role = "error";
					answer.text = message;
					useToast().error(message);
				},
			},
			context,
		);
	} catch (error: any) {
		const message = error?.message ?? "Er is iets misgegaan.";
		answer.role = "error";
		answer.text = message;
		useToast().error(message);
	} finally {
		isBusy.value = false;
		scrollToBottom();
	}
}

function startNewChat() {
	if (isBusy.value) return;
	store.newChat();
	messages.value = [];
	searchValue.value = "";
	nextTick(() => searchInput.value?.focus());
}

async function selectConversation(id: string) {
	if (isBusy.value || id === activeConversationId.value) return;
	store.select(id);
	try {
		const transcript = await getTranscript(id);
		messages.value = transcript.messages.map((message) => ({
			role: message.role,
			text: message.content,
			html: message.role === "assistant" ? md.render(message.content) : undefined,
			steps: [],
		}));
	} catch {
		messages.value = [];
		useToast().error("Kon gesprek niet laden.");
	}
	scrollToBottom();
}

async function removeConversation(id: string) {
	if (isBusy.value) return;
	const wasActive = id === activeConversationId.value;
	try {
		await store.remove(id);
		if (wasActive) messages.value = [];
	} catch {
		useToast().error("Kon gesprek niet verwijderen.");
	}
}

function scrollToBottom() {
	nextTick(() => {
		const element = transcriptEl.value;
		if (element) element.scrollTop = element.scrollHeight;
	});
}
</script>

<style scoped>
.context-chip {
  @apply flex items-center gap-2 max-w-full px-3 py-1.5 text-xs text-left
    bg-body-lighter text-body-content border-t border-body-darker
    cursor-pointer hover:text-primary disabled:cursor-default disabled:hover:text-body-content;
}

.context-chip--off {
  @apply opacity-50 line-through;
}

.msg-row {
  @apply flex flex-col gap-1 max-w-[85%];
}

.msg-row--assistant {
  @apply self-start items-start;
}

.msg-row--user {
  @apply self-end items-end;
}

.message {
  @apply text-sm rounded px-3 py-2 whitespace-pre-wrap;
}

.message--user {
  @apply bg-primary text-white;
}

.message--assistant {
  @apply bg-body-darker text-body-content whitespace-normal;
}

.message--error {
  @apply bg-danger-lighter text-danger-content;
}

.step-bubble {
  @apply self-start flex items-center gap-2 text-xs rounded px-3 py-1.5 bg-body-lighter text-body-content opacity-80;
}

.steps-toggle {
  @apply self-start text-xs opacity-70;
}

.steps-toggle > summary {
  @apply cursor-pointer select-none hover:text-primary;
}

.conversation {
  @apply flex items-center rounded border border-transparent hover:border-body-darker;
}

.conversation--active {
  @apply border-primary bg-body-darker;
}

.conversation__title {
  @apply grow truncate text-left text-sm px-2 py-2 bg-transparent text-body-content focus:outline-none cursor-pointer disabled:cursor-default;
}

.conversation__delete {
  @apply px-2 h-3.5 shrink-0 cursor-pointer opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-primary;
}

.markdown :deep(p) {
  @apply my-1;
}

.markdown :deep(ul) {
  @apply list-disc pl-5 my-1;
}

.markdown :deep(ol) {
  @apply list-decimal pl-5 my-1;
}

.markdown :deep(code) {
  @apply font-mono text-xs bg-body px-1 rounded;
}

.markdown :deep(table) {
  @apply border-collapse my-1;
}

.markdown :deep(th),
.markdown :deep(td) {
  @apply border border-body-darker px-2 py-1 text-left;
}
</style>
